// Operator buttons shown beside a conversation.
// Route: /settings/custom-actions

import { useEffect, useState, type ReactNode } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  Search,
  Table,
  Toggle,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  PAGE_SIZE,
  errMessage,
  ErrorBanner,
  useDebounced,
  fmtDate,
  HeadersEditor,
} from "./shared";

export type CustomActionType = "webhook" | "url" | "javascript";

export interface CustomActionConfig {
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  open_in_new_tab?: boolean;
  code?: string;
}

export interface CustomActionRow {
  id: string;
  name: string;
  icon: string;
  action_type: CustomActionType;
  config: CustomActionConfig;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface CustomActionListResponse {
  custom_actions: CustomActionRow[];
  total: number;
  page: number;
  limit: number;
}

// ============================================================ CustomActionsView

const ACTION_ICONS: { value: string; label: string; glyph: string }[] = [
  { value: "ticket", label: "Ticket", glyph: "⌗" },
  { value: "user", label: "User", glyph: "◍" },
  { value: "bar-chart", label: "Chart", glyph: "▦" },
  { value: "link", label: "Link", glyph: "⛓" },
  { value: "phone", label: "Phone", glyph: "☎" },
  { value: "mail", label: "Mail", glyph: "✉" },
  { value: "file-text", label: "Document", glyph: "▤" },
  { value: "external-link", label: "External", glyph: "↗" },
  { value: "zap", label: "Zap", glyph: "✦" },
  { value: "globe", label: "Globe", glyph: "◎" },
  { value: "code", label: "Code", glyph: "⌘" },
];

const actionGlyph = (icon: string) =>
  ACTION_ICONS.find((i) => i.value === icon)?.glyph ?? "✦";

const ACTION_TYPE_LABEL: Record<CustomActionType, string> = {
  webhook: "Webhook",
  url: "URL",
  javascript: "JavaScript",
};

const BODY_TEMPLATE = `{
  "subject": "WhatsApp: {{contact.name}}",
  "phone": "{{contact.phone_number}}",
  "description": "Contact from WhatsApp",
  "user": "{{user.name}}"
}`;

interface CustomActionForm {
  name: string;
  icon: string;
  action_type: CustomActionType;
  is_active: boolean;
  display_order: number;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  open_in_new_tab: boolean;
  code: string;
}

const emptyActionForm = (order: number): CustomActionForm => ({
  name: "",
  icon: "zap",
  action_type: "webhook",
  is_active: true,
  display_order: order,
  url: "",
  method: "POST",
  headers: {},
  body: "",
  open_in_new_tab: true,
  code: "",
});

export function CustomActionsView() {
  const { can } = useSession();
  const toast = useToast();
  const canWrite = can("custom_actions", "write");
  const canDeleteAction = can("custom_actions", "delete");

  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [query]);

  const { data, error, loading, reload } = useAsync<CustomActionListResponse>(
    () =>
      http.get<CustomActionListResponse>(`/custom-actions${qs({ search: query, page, limit: PAGE_SIZE })}`),
    [query, page]
  );
  const actions = data?.custom_actions ?? [];
  const total = data?.total ?? actions.length;

  const [editing, setEditing] = useState<CustomActionRow | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CustomActionForm>(emptyActionForm(0));
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<CustomActionRow | null>(null);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyActionForm(actions.length));
    setDialogOpen(true);
  };

  const openEdit = (action: CustomActionRow) => {
    setEditing(action);
    setForm({
      name: action.name,
      icon: action.icon || "zap",
      action_type: action.action_type,
      is_active: action.is_active,
      display_order: action.display_order,
      url: action.config.url ?? "",
      method: action.config.method ?? "POST",
      headers: { ...(action.config.headers ?? {}) },
      body: action.config.body ?? "",
      open_in_new_tab: action.config.open_in_new_tab !== false,
      code: action.config.code ?? "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.push("Enter a name", "critical");
      return;
    }
    if ((form.action_type === "webhook" || form.action_type === "url") && !form.url.trim()) {
      toast.push("Enter the URL", "critical");
      return;
    }
    if (form.action_type === "javascript" && !form.code.trim()) {
      toast.push("Enter the script", "critical");
      return;
    }

    // The config is rebuilt per type: keys belonging to the other two types are
    // dropped rather than carried along as dead configuration.
    let config: CustomActionConfig;
    if (form.action_type === "webhook") {
      config = {
        url: form.url.trim(),
        method: form.method,
        headers: form.headers,
        body: form.body.trim(),
      };
    } else if (form.action_type === "url") {
      config = { url: form.url.trim(), open_in_new_tab: form.open_in_new_tab };
    } else {
      config = { code: form.code };
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        icon: form.icon,
        action_type: form.action_type,
        config,
        is_active: form.is_active,
        display_order: form.display_order,
      };
      if (editing) {
        await http.put(`/custom-actions/${editing.id}`, payload);
        toast.push("Action updated", "good");
      } else {
        await http.post("/custom-actions", payload);
        toast.push("Action created", "good");
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not save the action"), "critical");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (action: CustomActionRow) => {
    try {
      // display_order is overwritten from the body on every PUT, so omitting it
      // here would quietly move the action to the front of the inbox toolbar.
      await http.put(`/custom-actions/${action.id}`, {
        is_active: !action.is_active,
        display_order: action.display_order,
      });
      toast.push(action.is_active ? "Action disabled" : "Action enabled", "good");
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not change the action"), "critical");
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/custom-actions/${toDelete.id}`);
      toast.push("Action deleted", "good");
      setToDelete(null);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not delete the action"), "critical");
      throw e;
    }
  };

  const columns: Column<CustomActionRow>[] = [
    {
      key: "icon",
      header: "",
      width: "40px",
      render: (a) => (
        <span className="muted" aria-hidden title={a.icon}>
          {actionGlyph(a.icon)}
        </span>
      ),
    },
    { key: "name", header: "Name", sortable: true, render: (a) => <span className="strong">{a.name}</span> },
    {
      key: "action_type",
      header: "Type",
      sortable: true,
      render: (a) => <Badge>{ACTION_TYPE_LABEL[a.action_type] ?? a.action_type}</Badge>,
    },
    {
      key: "target",
      header: "Target",
      render: (a) => (
        <span className="muted truncate" style={{ maxWidth: 200, display: "inline-block" }}>
          {a.action_type === "javascript" ? "Custom script" : a.config.url || "—"}
        </span>
      ),
    },
    {
      key: "is_active",
      header: "Status",
      sortable: true,
      render: (a) => (
        <div className="row">
          <Toggle
            checked={a.is_active}
            disabled={!canWrite}
            ariaLabel={`${a.is_active ? "Disable" : "Enable"} ${a.name}`}
            onChange={() => void toggle(a)}
          />
          <span className="muted">{a.is_active ? "Active" : "Inactive"}</span>
        </div>
      ),
    },
    { key: "created_at", header: "Created", sortable: true, render: (a) => fmtDate(a.created_at) },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (a) => (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button
            className="btn icon sm ghost"
            title="Edit this action"
            aria-label={`Edit ${a.name}`}
            disabled={!canWrite}
            onClick={() => openEdit(a)}
          >
            ✎
          </button>
          <button
            className="btn icon sm ghost"
            title="Delete this action"
            aria-label={`Delete ${a.name}`}
            disabled={!canDeleteAction}
            onClick={() => setToDelete(a)}
          >
            ✕
          </button>
        </div>
      ),
    },
  ];

  const typeChoice = (value: CustomActionType, label: string, hint: string): ReactNode => (
    <label className="check" key={value} style={{ alignItems: "flex-start" }}>
      <input
        type="radio"
        name="custom-action-type"
        checked={form.action_type === value}
        onChange={() => setForm({ ...form, action_type: value })}
      />
      <span>
        <span className="strong">{label}</span>
        <span className="muted" style={{ display: "block" }}>
          {hint}
        </span>
      </span>
    </label>
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Custom actions</h1>
          <div className="sub">Extra buttons operators get on a conversation.</div>
        </div>
        <div className="page-actions">
          <button className="btn primary" disabled={!canWrite} onClick={openCreate}>
            + Add action
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the custom actions" />

      <div className="card flush">
        <div className="card-head">
          <h3>Your actions</h3>
          <Search value={search} onChange={setSearch} placeholder="Search actions…" />
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            rows={actions}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching actions" : "No custom actions yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "Add a button that raises a ticket, opens a record, or runs a short script against the contact in view."
                }
                icon="✦"
                action={
                  !query && canWrite ? (
                    <button className="btn primary sm" onClick={openCreate}>
                      + Add action
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="actions"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Edit custom action" : "Create custom action"}
      >
        <Field label="Name" required>
          <input
            value={form.name}
            placeholder="Raise a ticket"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>

        <div className="field" role="group" aria-label="Icon">
          <div className="group-label">Icon</div>
          <div className="chips">
            {ACTION_ICONS.map((icon) => (
              <button
                key={icon.value}
                className={`btn icon${form.icon === icon.value ? " primary" : ""}`}
                title={icon.label}
                aria-label={icon.label}
                aria-pressed={form.icon === icon.value}
                onClick={() => setForm({ ...form, icon: icon.value })}
              >
                {icon.glyph}
              </button>
            ))}
          </div>
        </div>

        <div className="field" role="radiogroup" aria-label="Action type">
          <div className="group-label">Action type</div>
          <div className="col">
            {typeChoice("webhook", "Webhook", "Call an HTTP endpoint from the server.")}
            {typeChoice("url", "URL", "Open a link with the contact's details filled in.")}
            {typeChoice(
              "javascript",
              "JavaScript",
              "Run a short script. It executes on the server, not in the browser."
            )}
          </div>
        </div>

        {form.action_type === "webhook" && (
          <>
            <Field label="Webhook URL" required>
              <input
                type="url"
                value={form.url}
                placeholder="https://example.com/tickets"
                onChange={(e) => setForm({ ...form, url: e.target.value })}
              />
            </Field>
            <Field label="HTTP method">
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                <option value="POST">POST</option>
                <option value="GET">GET</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
              </select>
            </Field>
            <div className="field">
              <div className="group-label">Headers</div>
              <HeadersEditor
                headers={form.headers}
                onChange={(h) => setForm({ ...form, headers: h })}
              />
            </div>
            <div className="field">
              <div className="row">
                <div className="group-label spacer">Request body</div>
                <button className="btn sm ghost" onClick={() => setForm({ ...form, body: BODY_TEMPLATE })}>
                  Insert template
                </button>
              </div>
              <textarea
                value={form.body}
                rows={6}
                className="mono"
                placeholder='{"subject": "{{contact.name}}"}'
                aria-label="Request body"
                onChange={(e) => setForm({ ...form, body: e.target.value })}
              />
              <span className="hint">
                {"{{contact.name}}, {{contact.phone_number}} and {{user.name}} are substituted before the call."}
              </span>
            </div>
          </>
        )}

        {form.action_type === "url" && (
          <>
            <Field
              label="URL"
              required
              hint="Contact and user variables are substituted the same way as in a webhook body."
            >
              <input
                type="url"
                value={form.url}
                placeholder="https://crm.example.com/search?q={{contact.phone_number}}"
                onChange={(e) => setForm({ ...form, url: e.target.value })}
              />
            </Field>
            <div className="setting">
              <div className="text">
                <div className="strong">Open in a new tab</div>
                <p>Keeps the conversation open behind it.</p>
              </div>
              <Toggle
                checked={form.open_in_new_tab}
                ariaLabel="Open in a new tab"
                onChange={(v) => setForm({ ...form, open_in_new_tab: v })}
              />
            </div>
          </>
        )}

        {form.action_type === "javascript" && (
          <div className="field">
            <div className="group-label">Script</div>
            <textarea
              value={form.code}
              rows={10}
              className="mono"
              aria-label="Script"
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
            <span className="hint">
              Runs server-side. Return an object with any of <code>toast</code>, <code>clipboard</code>,{" "}
              <code>url</code> or <code>message</code> to tell the console what to do next.
            </span>
          </div>
        )}

        <div className="setting">
          <div className="text">
            <div className="strong">Active</div>
            <p>Only active actions appear on a conversation.</p>
          </div>
          <Toggle
            checked={form.is_active}
            ariaLabel="Action active"
            onChange={(v) => setForm({ ...form, is_active: v })}
          />
        </div>

        <DialogFooter>
          <button className="btn" onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={saving}>
            {saving && <span className="spinner" />}
            {editing ? "Update" : "Create"}
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
        danger
        title="Delete custom action"
        body={
          <>
            <strong className="strong">{toDelete?.name}</strong> disappears from every conversation.
            This cannot be undone.
          </>
        }
      />
    </div>
  );
}
